import { readBlobAsDataUrl } from './dataUrl';

// Voice notes for the Sissi AI composer. Limits and the MIME list are copied
// from the patient app (EternalFrontend/app/utils/audioUtils.ts) so both
// clients refuse the same things the backend refuses, before sending.

// MIME types the backend transcribes (api/services/openai_service.py,
// AUDIO_EXTENSION_BY_MIME). Anything else is rejected server-side, so the
// recorder never starts with a type outside this list.
export const ACCEPTED_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/mpga',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/aac',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/ogg',
] as const;

// Ceiling the backend enforces on the base64 `file_data` string (~15 MB of
// decoded audio). The duration cap keeps a note far below it; this only
// catches pathological cases.
export const MAX_AUDIO_BASE64_CHARS = 20_000_000;

// Recording length cap. Long notes risk the deployment's WebSocket frame
// limit; the app stops at the same point.
export const MAX_RECORDING_MS = 120_000;

// Anything shorter is almost always an accidental click, and sending it still
// costs a transcription and an agent turn that answers nothing.
export const MIN_RECORDING_MS = 3_000;

// How long before the cap the timer starts warning.
export const CAP_WARNING_MS = 15_000;

// Mono speech bitrate: the app records 32 kbps AAC; Opus at 32 kbps is at
// least as intelligible. 120 s ≈ 480 KB raw, ≈ 650 KB as base64.
export const RECORDING_BITS_PER_SECOND = 32_000;

// Probed in order; the first one MediaRecorder supports wins. Chrome, Edge and
// Firefox record Opus in WebM; Safari records AAC in MP4 (it has no WebM
// encoder); Firefox can fall back to Ogg. Every candidate strips to a MIME on
// the accepted list.
const RECORDING_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

const EXTENSION_BY_MIME: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
};

// Strips parameters and casing so "Audio/WEBM;codecs=opus" compares equal to
// "audio/webm". This is the spelling sent as file_type and stored by the backend.
export function normalizeAudioMimeType(fileType: string): string {
  return fileType.split(';')[0].trim().toLowerCase();
}

export function isAcceptedAudioMimeType(fileType: string): boolean {
  return (ACCEPTED_AUDIO_MIME_TYPES as readonly string[]).includes(normalizeAudioMimeType(fileType));
}

// True when a chat row's attachment is audio rather than an image or a PDF.
export function isAudioFileType(fileType: string | undefined | null): boolean {
  return !!fileType && normalizeAudioMimeType(fileType).startsWith('audio/');
}

// The MediaRecorder mimeType to record with (with codec parameters, as the
// recorder wants it), or null when the browser supports none we accept.
export function pickRecordingMimeType(
  isTypeSupported: (type: string) => boolean = (type) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type),
): string | null {
  for (const candidate of RECORDING_MIME_CANDIDATES) {
    if (!isAcceptedAudioMimeType(candidate)) continue;
    try {
      if (isTypeSupported(candidate)) return candidate;
    } catch {
      // isTypeSupported throws on some older engines; treat as unsupported.
    }
  }
  return null;
}

// False on browsers without MediaRecorder or getUserMedia (including insecure
// http origins, where getUserMedia is not exposed) and on browsers whose
// recorder produces nothing the backend accepts.
export function isVoiceRecordingSupported(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    return false;
  }
  if (typeof MediaRecorder === 'undefined') return false;
  return pickRecordingMimeType() !== null;
}

// Filename sent along with the note. The backend ignores it for audio (only
// PDFs carry file_name); the composer's file shape just needs one.
export function audioFileName(mimeType: string): string {
  return `nota-de-voz.${EXTENSION_BY_MIME[normalizeAudioMimeType(mimeType)] ?? 'webm'}`;
}

// Encodes a recording as the data URL the socket carries as file_data. The
// prefix is rebuilt from the normalised MIME: a blob typed
// "audio/webm;codecs=opus" would otherwise put the codec parameter into the
// URL, and the backend stores file_type as sent.
export async function blobToDataUrl(blob: Blob, mimeType: string): Promise<string> {
  const result = await readBlobAsDataUrl(blob);
  const comma = result.indexOf(',');
  if (comma === -1) throw new Error('Unexpected FileReader result');
  return `data:${normalizeAudioMimeType(mimeType)};base64,${result.slice(comma + 1)}`;
}

// "m:ss" for the recording timer and the bubble's duration label.
export function formatAudioDuration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
